import { useMemo } from 'react';
import { useGetPickemGoogleAd } from './useTenant';
import { useTenant } from '@/contexts/TenantContext';
import { deriveDivIdPrefix } from '@/config/ads.config';
import type { GoogleAdsPlacement } from './useTenant';

/**
 * Hook to fetch and transform pickem-specific contest card ad placement
 * @param pickemId - The pickem ID
 * @returns Object with transformed ad placement and whether to show the ad
 */
export const usePickemContestCardAd = (pickemId: string | null | undefined) => {
  const { isAdsEnabled } = useTenant();
  const { data: pickemAdPlacement } = useGetPickemGoogleAd(pickemId);

  // Transform pickem ad placement to AdPlacement format
  const transformedAdPlacement = useMemo((): GoogleAdsPlacement | null => {
    if (!pickemAdPlacement || !pickemAdPlacement.enabled || !pickemAdPlacement.adUnitPath) {
      return null;
    }

    const divIdPrefix = deriveDivIdPrefix(pickemAdPlacement as GoogleAdsPlacement);

    if (!divIdPrefix) {
      return null;
    }

    // Filter out empty targeting values
    const targeting = pickemAdPlacement.targeting
      ? Object.fromEntries(
          Object.entries(pickemAdPlacement.targeting).filter(
            ([_, value]) => value && value.trim() !== ''
          )
        )
      : undefined;

    return {
      placementKey: 'PICKEM_CONTEST_CARD',
      enabled: pickemAdPlacement.enabled,
      adUnitPath: pickemAdPlacement.adUnitPath,
      sizes: pickemAdPlacement.sizes || [
        [320, 50],
        [728, 90],
      ],
      sizeMappings: pickemAdPlacement.sizeMappings,
      divIdPrefix,
      targeting,
    };
  }, [pickemAdPlacement]);

  const shouldShowAd = isAdsEnabled && transformedAdPlacement && transformedAdPlacement.enabled;

  return {
    adPlacement: transformedAdPlacement,
    shouldShowAd,
  };
};
